import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { map } from 'rxjs';

const FORBIDDEN_PAYLOAD_KEYS = new Set(['elist', 'evmap']);
const OEM_ENTITY = 'cu_supp_pfg_oem';
const OEM_PART_TYPE_ATTRIBUTE = 'part_type';
const OEM_SALEABLE_ATTRIBUTE = 'saleable';
const OEM_CUSTOMER_ATTRIBUTE = 'customer_no';
const OEM_BILL_TO_ATTRIBUTE = 'bill_to_code';
const NESTED_DYNAMIC_READ_SERVICE = 'nesteddynamicreadservices';
const OEM_PART_TYPE_VALUES: Record<string, string[] | null> = {
  all_parts: null,
  saleable: ['Y', 'F'],
  non_saleable: ['N'],
};

/** Remove forbidden keys recursively without mutating the caller's payload. */
export function stripForbiddenPayloadKeys(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => stripForbiddenPayloadKeys(item));
  }

  if (!value || typeof value !== 'object' || Object.getPrototypeOf(value) !== Object.prototype) {
    return value;
  }

  return Object.entries(value).reduce<Record<string, unknown>>((cleaned, [key, item]) => {
    if (!FORBIDDEN_PAYLOAD_KEYS.has(key.toLowerCase())) {
      cleaned[key] = stripForbiddenPayloadKeys(item);
    }
    return cleaned;
  }, {});
}

/** Convert the OEM page's UI-only part selector into the API's saleable rule. */
export function normalizeOemPartTypeFilter(value: unknown): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }

  const payload = value as Record<string, any>;
  const requestData = payload['requestData'];
  if (
    !requestData ||
    typeof requestData !== 'object' ||
    String(requestData?.entity || '').trim().toLowerCase() !== OEM_ENTITY ||
    !Array.isArray(requestData?.filters)
  ) {
    return value;
  }

  const filters = requestData.filters.flatMap((filter: any) => {
    const attributeName = String(filter?.attributeName || '').trim().toLowerCase();
    if (attributeName === OEM_CUSTOMER_ATTRIBUTE) {
      return [{ ...filter, attributeName: OEM_BILL_TO_ATTRIBUTE }];
    }
    if (attributeName !== OEM_PART_TYPE_ATTRIBUTE) {
      return [filter];
    }

    const selectedPartType = String(filter?.attributeValue || '').trim().toLowerCase();
    const saleableValue = OEM_PART_TYPE_VALUES[selectedPartType];
    if (!saleableValue) {
      return [];
    }

    return [{
      attributeName: OEM_SALEABLE_ATTRIBUTE,
      operator: 'IN',
      logicalOperator: 'AND',
      beginScope: '',
      endScope: '',
      attributeValue: saleableValue,
    }];
  });

  return {
    ...payload,
    requestData: {
      ...requestData,
      filters,
    },
  };
}

/** Identify requests issued by the OEM table's three-way part selector. */
export function isOemPartTypeTableRequest(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const requestData = (value as any)?.requestData;
  const endpoints = (value as any)?.endpoints;
  return String(requestData?.entity || '').trim().toLowerCase() === OEM_ENTITY &&
    Array.isArray(requestData?.filters) &&
    requestData.filters.some((filter: any) =>
      String(filter?.attributeName || '').trim().toLowerCase() === OEM_PART_TYPE_ATTRIBUTE
    ) &&
    Array.isArray(endpoints) &&
    endpoints.some((endpoint: any) =>
      String(endpoint?.functionName || '').trim().toLowerCase() === NESTED_DYNAMIC_READ_SERVICE
    );
}

/** Resolve every supported platform response envelope into rows for the OEM smart table. */
export function extractOemTableRows(value: unknown): Record<string, unknown>[] {
  const seen = new WeakSet<object>();

  const normalizeRows = (candidate: any): Record<string, unknown>[] => {
    if (!Array.isArray(candidate)) {
      return [];
    }
    return candidate.flatMap((row): Record<string, unknown>[] => {
      if (!row || typeof row !== 'object' || Array.isArray(row)) {
        return [];
      }
      if (Array.isArray(row.data)) {
        return normalizeRows(row.data);
      }
      if (row.data && typeof row.data === 'object') {
        return [row.data];
      }
      return [row];
    });
  };

  const findEntityRows = (candidate: any): Record<string, unknown>[] => {
    if (!candidate || typeof candidate !== 'object' || seen.has(candidate)) {
      return [];
    }
    seen.add(candidate);

    if (!Array.isArray(candidate)) {
      const entityEntry = Object.entries(candidate).find(([key]) =>
        key.trim().toLowerCase() === OEM_ENTITY
      );
      if (entityEntry) {
        const rows = normalizeRows(entityEntry[1]);
        if (rows.length > 0) {
          return rows;
        }
      }
    }

    const nestedValues = Array.isArray(candidate) ? candidate : Object.values(candidate);
    for (const nested of nestedValues) {
      const rows = findEntityRows(nested);
      if (rows.length > 0) {
        return rows;
      }
    }
    return [];
  };

  const entityRows = findEntityRows(value);
  if (entityRows.length > 0) {
    return entityRows;
  }

  const body = value as any;
  const fallbackCandidates = [
    body?.tableRows,
    body?.rows,
    body?.data,
    body?.responseList?.[0]?.data,
  ];
  for (const candidate of fallbackCandidates) {
    const rows = normalizeRows(candidate);
    if (rows.length > 0) {
      return rows;
    }
  }

  return Array.isArray(value) ? normalizeRows(value) : [];
}

/** Ensure generated record-list fields never leave the application in a request body. */
export const requestPayloadInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.body === null || req.body === undefined) {
    return next(req);
  }

  const normalizeOemTableResponse = isOemPartTypeTableRequest(req.body);
  const cleanedBody = stripForbiddenPayloadKeys(req.body);
  return next(req.clone({ body: normalizeOemPartTypeFilter(cleanedBody) })).pipe(
    map((event) => {
      if (!normalizeOemTableResponse || !(event instanceof HttpResponse)) {
        return event;
      }

      return event.clone({
        body: { tableRows: extractOemTableRows(event.body) },
      });
    }),
  );
};
